import React, { useState } from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../Components/Core/SeoHead";
import { useTenantContent } from "../../Context/TenantContentContext";
import Loader from "../../Components/Core/Loader";
import Footer from "../../Components/Core/InfoFooter";
import {
  buildPhysicianJsonLd,
  doctorDisplayName,
  joinUrl,
  resolveAboutPageTitle,
  resolveMediaUrl,
} from "../../utils/seo";
import "./About.css";

const labels = {
  fa: {
    education: "تحصیلات",
    academicDegree: "رتبه علمی",
    experience: "سوابق حرفه‌ای",
    publications: "مقالات چاپ‌شده",
    responsibilities: "مسئولیت‌های اجرایی",
    contact: "اطلاعات تماس",
    workplace: "محل کار",
    clinic: "آدرس مطب",
    bio: "معرفی پزشک",
    years: "سال تجربه",
    patients: "بیمار",
    reserve: "رزرو نوبت",
    counseling: "مشاوره آنلاین",
  },
  en: {
    education: "Education",
    academicDegree: "Academic Rank",
    experience: "Professional Experience",
    publications: "Published Articles",
    responsibilities: "Executive Roles",
    contact: "Contact",
    workplace: "Workplace",
    clinic: "Clinic Address",
    bio: "Biography",
    years: "Years of experience",
    patients: "Patients",
    reserve: "Book appointment",
    counseling: "Online consult",
  },
};

const toLatinDigits = (value) =>
  String(value ?? "")
    .replace(/[\u06F0-\u06F9]/g, (ch) => String(ch.charCodeAt(0) - 0x06f0))
    .replace(/[\u0660-\u0669]/g, (ch) => String(ch.charCodeAt(0) - 0x0660));

const Icon = ({ path }) => (
  <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
    <path d={path} />
  </svg>
);

const About = () => {
  const [language, setLanguage] = useState("fa");
  const { content, loading } = useTenantContent();

  if (loading || !content) {
    return <Loader />;
  }

  const t = labels[language] || labels.fa;
  const isEnglish = language === "en";
  const isRTL = !isEnglish;
  const pickText = (fa, en) =>
    isEnglish ? en || fa || "" : fa || "";
  const show = (value) => (isEnglish ? toLatinDigits(value) : value);
  const localizedList = (items) =>
    (items || [])
      .map((item) => {
        if (!item) return "";
        if (typeof item === "string") return item;
        return isEnglish
          ? item.textEn || item.textFa || ""
          : item.textFa || item.textEn || "";
      })
      .filter(Boolean)
      .map(show);
  const pageUrl = joinUrl(content.canonicalUrl, "about");
  const doctorNameFa = doctorDisplayName(content);
  const doctorNameEnRaw = content.doctorNameEn || content.doctorName || "";
  const doctorName = isEnglish
    ? doctorNameEnRaw
      ? /^dr\.?\b/i.test(doctorNameEnRaw.trim())
        ? doctorNameEnRaw
        : `Dr. ${doctorNameEnRaw}`
      : "Physician"
    : doctorNameFa;
  const pageTitle = isEnglish
    ? `About ${doctorName} | ${content.siteName || ""}`.trim()
    : resolveAboutPageTitle(content);
  const photo = content.doctorAvatarUrl || content.doctorImageUrl;
  const bioText =
    pickText(content.aboutPageContent, content.aboutPageContentEn) ||
    pickText(content.homeAboutSectionText, content.homeAboutSectionTextEn) ||
    pickText(content.experienceText, content.experienceTextEn);
  const pageDescription =
    pickText(content.homeAboutSectionText, content.homeAboutSectionTextEn) ||
    pickText(content.experienceText, content.experienceTextEn) ||
    content.siteDescription;
  const ogImage = photo || content.ogImage;
  const educationItems = localizedList(content.educationItems);
  const academicItems = localizedList(content.academicDegrees);
  const responsibilityItems = localizedList(content.responsibilities);
  const experienceText = pickText(
    content.experienceText,
    content.experienceTextEn,
  );
  const publicationsText = pickText(
    content.publicationsText,
    content.publicationsTextEn,
  );
  const workplaceInfo = pickText(
    content.workplaceInfo,
    content.workplaceInfoEn,
  );
  const clinicInfo = pickText(
    content.clinicInfo || content.address,
    content.clinicInfoEn || content.addressEn,
  );
  const doctorTitle = pickText(content.doctorTitle, content.doctorTitleEn);
  const doctorSpecialty = pickText(
    content.doctorSpecialty,
    content.doctorSpecialtyEn,
  );
  const hasContact = Boolean(
    workplaceInfo ||
      clinicInfo ||
      content.workplacePhone ||
      content.clinicPhone ||
      content.email
  );
  const showReserve = content.isReservationPageEnabled !== false;
  const showCounseling = content.isConsultationPageEnabled !== false;

  return (
    <>
      <SeoHead
        title={pageTitle}
        description={pageDescription}
        keywords={content.siteKeywords}
        canonical={pageUrl}
        image={ogImage}
        imageAlt={doctorName}
        siteName={content.siteName}
        jsonLd={buildPhysicianJsonLd(content, pageUrl)}
      />

      <div className={`about-page ${isRTL ? "rtl" : "ltr"}`} dir={isRTL ? "rtl" : "ltr"}>
        <section className="about-hero">
          <div className="container">
            <div className="about-lang">
              <button
                type="button"
                className={`about-lang-btn ${language === "fa" ? "is-active" : ""}`}
                onClick={() => setLanguage("fa")}
              >
                فا
              </button>
              <button
                type="button"
                className={`about-lang-btn ${language === "en" ? "is-active" : ""}`}
                onClick={() => setLanguage("en")}
              >
                EN
              </button>
            </div>

            <div className="row align-items-center">
              <div className="col-lg-4">
                <div className="about-photo-wrap">
                  {photo ? (
                    <img
                      className="about-photo"
                      src={resolveMediaUrl(photo)}
                      alt={doctorName}
                    />
                  ) : (
                    <div className="about-photo-fallback" aria-hidden="true">
                      ⚕
                    </div>
                  )}
                </div>
              </div>

              <div className="col-lg-8">
                <div className="about-hero-copy">
                  <h1 className="about-hero-title">{show(doctorName)}</h1>
                  {doctorTitle ? (
                    <p className="about-hero-role">{show(doctorTitle)}</p>
                  ) : null}
                  {doctorSpecialty ? (
                    <p className="about-hero-specialty">
                      {show(doctorSpecialty)}
                    </p>
                  ) : null}

                  <div className="about-chips">
                    {content.showYearExprience && content.yearExprience ? (
                      <span className="about-chip">
                        {show(content.yearExprience)}+ {t.years}
                      </span>
                    ) : null}
                    {content.showPatientCount && content.patientCount ? (
                      <span className="about-chip">
                        {show(content.patientCount)}+ {t.patients}
                      </span>
                    ) : null}
                  </div>

                  {(showReserve || showCounseling) && (
                    <div className="about-actions">
                      {showReserve ? (
                        <Link
                          className="about-action about-action-primary"
                          to="/reservation/reserve"
                        >
                          {t.reserve}
                        </Link>
                      ) : null}
                      {showCounseling ? (
                        <Link
                          className="about-action about-action-ghost"
                          to="/counseling/index"
                        >
                          {t.counseling}
                        </Link>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="container about-main">
          {bioText ? (
            <article className="about-bio">
              <span className="about-kicker">{t.bio}</span>
              <p className="about-bio-text">{show(bioText)}</p>
            </article>
          ) : null}

          <div className="row">
            {educationItems.length > 0 && (
              <div className="col-lg-6 mb-4">
                <section className="about-card">
                  <h2 className="about-card-title">
                    <span className="about-icon about-icon-blue">
                      <Icon d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9z" />
                    </span>
                    {t.education}
                  </h2>
                  <ul className="about-list">
                    {educationItems.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </section>
              </div>
            )}

            {academicItems.length > 0 && (
              <div className="col-lg-6 mb-4">
                <section className="about-card">
                  <h2 className="about-card-title">
                    <span className="about-icon about-icon-green">
                      <Icon d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </span>
                    {t.academicDegree}
                  </h2>
                  <ul className="about-list">
                    {academicItems.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </section>
              </div>
            )}
          </div>

          {experienceText ? (
            <section className="about-panel">
              <h2 className="about-panel-title">
                <span className="about-icon about-icon-green">
                  <Icon d="M20 6h-2.18c.11-.31.18-.65.18-1 0-1.66-1.34-3-3-3-1.05 0-1.96.54-2.5 1.35l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2z" />
                </span>
                {t.experience}
              </h2>
              <p className="about-panel-text">{show(experienceText)}</p>
            </section>
          ) : null}

          <div className="row">
            {publicationsText ? (
              <div className="col-lg-6 mb-4">
                <section className="about-card">
                  <h2 className="about-card-title">
                    <span className="about-icon about-icon-green">
                      <Icon d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 2 2h8c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
                    </span>
                    {t.publications}
                  </h2>
                  <ul className="about-list">
                    <li style={{ whiteSpace: "pre-wrap" }}>
                      {show(publicationsText)}
                    </li>
                  </ul>
                </section>
              </div>
            ) : null}

            {responsibilityItems.length > 0 ? (
              <div className="col-lg-6 mb-4">
                <section className="about-card">
                  <h2 className="about-card-title">
                    <span className="about-icon about-icon-blue">
                      <Icon d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </span>
                    {t.responsibilities}
                  </h2>
                  <ul className="about-list">
                    {responsibilityItems.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </section>
              </div>
            ) : null}
          </div>

          {hasContact ? (
            <section className="about-contact">
              <h2 className="about-contact-title">{t.contact}</h2>
              <div className="row">
                {workplaceInfo ? (
                  <div className="col-md-6 mb-3">
                    <div className="about-contact-card">
                      <h4>{t.workplace}</h4>
                      <p>{show(workplaceInfo)}</p>
                      {content.workplacePhone ? (
                        <a
                          className="about-contact-link"
                          href={`tel:${toLatinDigits(content.workplacePhone)}`}
                        >
                          {show(content.workplacePhone)}
                        </a>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {clinicInfo ? (
                  <div className="col-md-6 mb-3">
                    <div className="about-contact-card">
                      <h4>{t.clinic}</h4>
                      <p>{show(clinicInfo)}</p>
                      {content.clinicPhone ? (
                        <a
                          className="about-contact-link"
                          href={`tel:${toLatinDigits(content.clinicPhone)}`}
                        >
                          {show(content.clinicPhone)}
                        </a>
                      ) : null}
                      {content.email ? (
                        <a
                          className="about-contact-link"
                          href={`mailto:${content.email}`}
                        >
                          {content.email}
                        </a>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            </section>
          ) : null}
        </div>
      </div>

      {content.showFooter && <Footer />}
    </>
  );
};

export default About;
